import {
  useDocumentTypesInSelectedDrive,
  useSupportedDocumentTypesInReactor,
} from "@powerhousedao/reactor-browser";
import {
  addDocumentType,
  removeDocumentType,
  setAppName,
  setAppStatus,
  setDocumentTypes,
  setDragAndDropEnabled,
} from "@powerhousedao/vetra/document-models/app-module";
import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useSelectedAppModuleDocument } from "../../../document-models/app-module/index.js";
import { StatusPill } from "../../components/index.js";
import { useDebounce } from "../../hooks/index.js";

const ALL_IN_DRIVE = "all-in-drive";
const ALL_IN_REACTOR = "all-in-reactor";
const ALLOW_ANY = "allow-any";

export const AppEditorForm = () => {
  const [document, dispatch] = useSelectedAppModuleDocument();
  const documentName = document.state.global.name;
  const status = document.state.global.status;
  const isDragAndDropEnabled = document.state.global.isDragAndDropEnabled;
  const allowedDocumentTypes = document.state.global.allowedDocumentTypes;
  const [appName, handleSetAppName] = useState(documentName);
  const [isConfirmed, setIsConfirmed] = useState(status === "CONFIRMED");
  const documentTypesInSelectedDrive = useDocumentTypesInSelectedDrive();
  const supportedDocumentTypesInReactor = useSupportedDocumentTypesInReactor();
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState(
    allowedDocumentTypes ?? [],
  );

  // Use the debounce hook for name changes

  const onNameChange = useCallback(
    (name: string) => {
      if (name === documentName) return;
      console.log("onNameChange", name);
      dispatch(setAppName({ name }));
    },
    [documentName, dispatch],
  );

  useDebounce(appName, onNameChange, 300);

  const onConfirm = () => {
    dispatch(setAppStatus({ status: "CONFIRMED" }));
  };

  const onDragAndDropToggle = (enabled: boolean) => {
    if (enabled === isDragAndDropEnabled) return;
    dispatch(setDragAndDropEnabled({ enabled }));
  };

  // Reset confirmation state if status changes back to DRAFT
  useEffect(() => {
    if (status === "DRAFT") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsConfirmed(false);
    }
  }, [status]);

  // Check if form should be read-only
  const isReadOnly = isConfirmed || status === "CONFIRMED";

  const handleConfirm = () => {
    if (appName.trim()) {
      setIsConfirmed(true); // Immediate UI update
      onConfirm();
    }
  };

  const handleAddDocumentType = (documentType: string) => {
    if (!documentType || selectedDocumentTypes.includes(documentType)) return;
    setSelectedDocumentTypes([...selectedDocumentTypes, documentType]);
    dispatch(addDocumentType({ documentType }));
  };

  const handleRemoveDocumentType = (documentType: string) => {
    setSelectedDocumentTypes(
      selectedDocumentTypes.filter((dt) => dt !== documentType),
    );
    dispatch(removeDocumentType({ documentType }));
  };

  const handleAddAllDocumentTypesInDrive = () => {
    const newDocumentTypes = [
      ...new Set([
        ...selectedDocumentTypes,
        ...(documentTypesInSelectedDrive ?? []),
      ]),
    ];
    setSelectedDocumentTypes(newDocumentTypes);
    dispatch(setDocumentTypes({ documentTypes: newDocumentTypes }));
  };

  const handleAddAllDocumentTypesInReactor = () => {
    const newDocumentTypes = [
      ...new Set([
        ...selectedDocumentTypes,
        ...(supportedDocumentTypesInReactor ?? []),
      ]),
    ];
    setSelectedDocumentTypes(newDocumentTypes);
    dispatch(setDocumentTypes({ documentTypes: newDocumentTypes }));
  };

  const handleAllowAnyDocumentType = () => {
    setSelectedDocumentTypes([]);
    dispatch(setDocumentTypes({ documentTypes: [] }));
  };

  const handleDocumentTypeSelection = (selectedValue: string) => {
    if (selectedValue === ALL_IN_DRIVE) {
      handleAddAllDocumentTypesInDrive();
    } else if (selectedValue === ALL_IN_REACTOR) {
      handleAddAllDocumentTypesInReactor();
    } else if (selectedValue === ALLOW_ANY) {
      handleAllowAnyDocumentType();
    } else {
      handleAddDocumentType(selectedValue);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-foreground">
          App Configuration
        </h2>
        <StatusPill
          status={status === "CONFIRMED" ? "confirmed" : "draft"}
          label={status === "CONFIRMED" ? "Confirmed" : "Draft"}
        />
      </div>

      {/* App Name Field */}
      <div>
        <label
          htmlFor="app-name"
          className="mb-2 block text-sm font-medium text-foreground"
        >
          App Name
        </label>
        <input
          id="app-name"
          type="text"
          value={appName}
          onChange={(e) => handleSetAppName(e.target.value)}
          disabled={isReadOnly}
          className={twMerge(
            "w-full rounded-md border border-border px-3 py-2 text-foreground placeholder:text-muted-foreground focus:border-transparent focus:ring-2 focus:ring-ring focus:outline-none disabled:disabled-effect",
            isReadOnly ? "cursor-not-allowed bg-muted" : "",
          )}
          placeholder="Enter app name"
        />
      </div>

      {/* Document Types Field */}
      <div>
        <label
          htmlFor="document-types"
          className="mb-2 block text-sm font-medium text-foreground"
        >
          Document Types
        </label>
        <div className="space-y-2">
          {!isReadOnly && (
            <select
              onChange={(e) => handleDocumentTypeSelection(e.target.value)}
              className="w-full rounded-md border border-border px-3 py-2 text-foreground focus:border-transparent focus:ring-2 focus:ring-ring focus:outline-none"
            >
              <option>Select a document type to add</option>
              <option>--- Vetra drive document types ---</option>
              <option value={ALL_IN_DRIVE}>
                Add all document types in Vetra drive
              </option>
              {documentTypesInSelectedDrive
                ?.filter((dt) => !selectedDocumentTypes.includes(dt))
                .map((docType) => (
                  <option key={docType} value={docType}>
                    {docType}
                  </option>
                ))}
              <option>--- Reactor document types ---</option>
              <option value={ALL_IN_REACTOR}>
                Add all document types in Reactor
              </option>
              {supportedDocumentTypesInReactor
                ?.filter((dt) => !selectedDocumentTypes.includes(dt))
                .map((docType) => (
                  <option key={docType} value={docType}>
                    {docType}
                  </option>
                ))}
              <option>--- Allow any document type ---</option>
              <option value={ALLOW_ANY}>Allow any document type</option>
            </select>
          )}
          <div className="space-y-1">
            {selectedDocumentTypes.length > 0 ? (
              selectedDocumentTypes.map((type) => (
                <div key={type} className="flex items-center py-1">
                  <span className="text-sm text-foreground">{type}</span>
                  {!isReadOnly && (
                    <button
                      onClick={() => handleRemoveDocumentType(type)}
                      className="ml-2 text-muted-foreground hover:hover-effect focus:outline-none"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))
            ) : (
              <span className="text-sm text-foreground">All documents (*)</span>
            )}
          </div>
        </div>
      </div>

      {/* Drag and Drop Settings */}
      <div>
        <h3 className="mb-4 text-base font-medium text-foreground">
          Drag and Drop Settings
        </h3>

        {/* Enable/Disable Switch */}
        <div className="mb-4">
          <label htmlFor="drag-and-drop-enabled" className="flex items-center">
            <input
              id="drag-and-drop-enabled"
              type="checkbox"
              checked={isDragAndDropEnabled}
              onChange={(e) => onDragAndDropToggle(e.target.checked)}
              disabled={isReadOnly}
              className={twMerge(
                "mr-2 size-4 rounded-sm border-border text-info focus:ring-ring",
                isReadOnly ? "cursor-not-allowed" : "",
              )}
            />
            <span className="text-sm font-medium text-foreground">
              Enable drag and drop
            </span>
          </label>
        </div>
      </div>

      {/* Confirm Button - only show if not in read-only mode */}
      {!isReadOnly && (
        <div>
          <button
            onClick={handleConfirm}
            disabled={!appName.trim()}
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:hover-effect focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:disabled-effect"
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
};
